import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Loader, X } from 'react-feather';

import {
  ApprovalStatus,
  PendingApprovalModalProps,
} from '@/Users/types/userTypes';

interface ExtendedPendingApprovalModalProps extends PendingApprovalModalProps {
  onFileDownload?: (imageUrl: string) => void;
  isDownloading?: boolean;
}

const PendingApprovalModal: React.FC<ExtendedPendingApprovalModalProps> = ({
  isOpen,
  onClose,
  selectedUser,
  onStatusUpdate,
  onFileDownload,
  isUpdating = false,
  isDownloading = false,
}) => {
  const [approvalStatus, setApprovalStatus] =
    useState<ApprovalStatus>('PENDING');

  useEffect(() => {
    if (selectedUser) {
      setApprovalStatus(selectedUser.approvalStatus);
    }
  }, [selectedUser]);

  if (!isOpen || !selectedUser) return null;

  const handleDownload = () => {
    if (onFileDownload && selectedUser.businessCertificateUrl) {
      onFileDownload(selectedUser.businessCertificateUrl);
    }
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setApprovalStatus(event.target.value as ApprovalStatus);
  };

  const handleSave = () => {
    if (onStatusUpdate && approvalStatus !== selectedUser.approvalStatus) {
      onStatusUpdate(selectedUser.uuid, approvalStatus, onClose);
    }
  };

  const isStatusChanged = approvalStatus !== selectedUser.approvalStatus;
  const isAnyActionInProgress = isUpdating || isDownloading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <main
        className="relative w-full max-w-[520px] rounded-lg bg-white shadow-lg"
        tabIndex={-1}
      >
        <header className="flex h-[72px] items-center justify-between px-6 py-6">
          <h3 id="modal-title" className="text-xl font-medium text-[#1d1d1d]">
            Pending Approval
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="bg-transparent hover:opacity-80 disabled:opacity-50"
            aria-label="Close modal"
            disabled={isAnyActionInProgress}
          >
            <X size={16} className="text-[#707070]" />
          </button>
        </header>

        <section className="rounded-b-lg bg-[#f1f1f1] px-6 py-6 text-left">
          <dl className="mb-7 flex flex-col gap-4">
            <div className="flex gap-4">
              <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                Request Date
              </dt>
              <dd className="text-sm font-normal text-[#444]">
                {selectedUser.createdAt &&
                !isNaN(new Date(selectedUser.createdAt).getTime())
                  ? format(new Date(selectedUser.createdAt), 'yyyy.MM.dd HH:mm')
                  : '-'}
              </dd>
            </div>
            {selectedUser.approvedAt &&
              !isNaN(new Date(selectedUser.approvedAt).getTime()) && (
                <div className="flex gap-4">
                  <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                    Approved Date
                  </dt>
                  <dd className="text-sm font-normal text-[#444]">
                    {format(
                      new Date(selectedUser.approvedAt),
                      'yyyy.MM.dd HH:mm',
                    )}
                  </dd>
                </div>
              )}
            <div className="flex gap-4">
              <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                Business Registration Number
              </dt>
              <dd className="text-sm font-normal text-[#444]">
                {selectedUser.businessRegistrationNumber}
              </dd>
            </div>
            <div className="flex gap-4">
              <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                Company Name
              </dt>
              <dd className="text-sm font-normal text-[#444]">
                {selectedUser.companyName}
              </dd>
            </div>
            <div className="flex gap-4">
              <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                Representative Name
              </dt>
              <dd className="text-sm font-normal text-[#444]">
                {selectedUser.representativeName}
              </dd>
            </div>
            <div className="flex gap-4">
              <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                Email
              </dt>
              <dd className="text-sm font-normal text-[#444]">
                {selectedUser.email}
              </dd>
            </div>
            <div className="flex gap-4">
              <dt className="min-w-[196px] text-sm font-medium text-[#1d1d1d]">
                Phone Number
              </dt>
              <dd className="text-sm font-normal text-[#444]">
                {selectedUser.phoneNumber}
              </dd>
            </div>
          </dl>

          <section className="flex flex-col gap-5">
            <div>
              <p className="mb-2 text-sm font-medium text-[#1d1d1d]">
                Certificate for Business Registration
              </p>
              <div className="flex gap-2">
                <div
                  className="flex w-[300px] items-center rounded border border-[#cecece] bg-white px-2 py-1.5 text-sm text-[#444]"
                  aria-label="Business Registration File Name"
                >
                  {selectedUser.businessRegistrationNumber}
                </div>
                <button
                  type="button"
                  className="flex h-8 w-24 items-center justify-center rounded bg-[#3e3e3e] text-sm font-light text-white hover:bg-[#222] disabled:opacity-50"
                  aria-label="Download business registration certificate"
                  onClick={handleDownload}
                  disabled={
                    isAnyActionInProgress ||
                    !selectedUser.businessCertificateUrl
                  }
                >
                  {isDownloading ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    'Download'
                  )}
                </button>
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-[#1d1d1d]">
                Approval Status
              </p>
              <select
                id="approvalStatus"
                aria-label="Approval Status"
                value={approvalStatus}
                onChange={handleStatusChange}
                className="w-[300px] rounded border border-[#cecece] py-1.5 pl-2 text-sm font-normal text-[#444] focus:outline-none disabled:opacity-50"
                disabled={isAnyActionInProgress}
              >
                <option value="PENDING">PENDING</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>
          </section>

          <footer className="mt-8 flex justify-end text-right">
            <button
              type="button"
              className="flex h-8 min-w-[60px] items-center justify-center rounded bg-[#3e3e3e] px-4 py-1 text-sm font-medium text-white hover:bg-[#222] disabled:opacity-50"
              aria-label="Save approval status"
              onClick={handleSave}
              disabled={isUpdating || !isStatusChanged}
            >
              {isUpdating ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                'Save'
              )}
            </button>
          </footer>
        </section>
      </main>
    </div>
  );
};

export default PendingApprovalModal;
